// ──────────────────────────────────────────────
// GeoDrops — Main App Component
// Orchestrates map, pins, sheets, and Telegram
// platform integration.
// ──────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import type { Pin, PinCategory, CreatePinInput } from "@/types";
import { useGeolocation } from "@/hooks/useGeolocation";
import { usePins } from "@/hooks/usePins";
import { Map } from "./Map";
import { CategoryFilter } from "./CategoryFilter";
import { PinDetail } from "./PinDetail";
import { CreatePin } from "./CreatePin";
import { LoadingScreen, ErrorScreen } from "./LoadingScreen";
import {
  ready,
  expand,
  getTheme,
  applyThemeToCSS,
  getStartParam,
  hapticImpact,
  hapticNotification,
} from "@/lib/telegram";

type View = "map" | "create" | "detail";

export function App() {
  const geo = useGeolocation();
  const pinStore = usePins();
  const [view, setView] = useState<View>("map");
  const [selectedPin, setSelectedPin] = useState<Pin | null>(null);
  const [createCoords, setCreateCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<PinCategory | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  // ── Initialize Telegram platform ──
  useEffect(() => {
    const theme = getTheme();
    applyThemeToCSS(theme);
    expand();
    ready();

    // Get current user ID from initDataUnsafe (display only — auth is server-side)
    const user = window.Telegram?.WebApp?.initDataUnsafe?.user;
    if (user) setCurrentUserId(user.id);

    // Listen for theme changes
    const handleThemeChange = () => {
      const newTheme = getTheme();
      applyThemeToCSS(newTheme);
    };
    window.Telegram?.WebApp?.onEvent?.("themeChanged", handleThemeChange);

    // Handle start param
    const startParam = getStartParam();
    if (startParam === "drop") {
      // Will open create sheet once location is available
    }

    return () => {
      window.Telegram?.WebApp?.offEvent?.("themeChanged", handleThemeChange);
    };
  }, []);

  // ── Load nearby pins when position changes ──
  useEffect(() => {
    if (geo.position) {
      pinStore.loadNearby(geo.position.lat, geo.position.lng, categoryFilter || undefined);
    }
  }, [geo.position?.lat, geo.position?.lng, categoryFilter]);

  // ── Auto-refresh pins every 30 seconds ──
  useEffect(() => {
    if (!geo.position) return;
    const interval = setInterval(() => {
      pinStore.loadNearby(geo.position!.lat, geo.position!.lng, categoryFilter || undefined);
    }, 30000);
    return () => clearInterval(interval);
  }, [geo.position, categoryFilter]);

  // ── Auto-open create flow if launched with ?startapp=drop ──
  useEffect(() => {
    const startParam = getStartParam();
    if (startParam === "drop" && geo.position && view === "map") {
      setCreateCoords({ lat: geo.position.lat, lng: geo.position.lng });
      setView("create");
    }
  }, [geo.position]);

  // ── Handlers ──

  const handlePinClick = useCallback((pin: Pin) => {
    hapticImpact("light");
    setSelectedPin(pin);
    setView("detail");
  }, []);

  const handleDropPin = useCallback((lat: number, lng: number) => {
    hapticImpact("medium");
    setCreateCoords({ lat, lng });
    setView("create");
  }, []);

  const handleCreateSubmit = useCallback(
    async (input: CreatePinInput) => {
      await pinStore.addPin(input);
      setView("map");
      setCreateCoords(null);
    },
    [pinStore.addPin]
  );

  const handleCreateCancel = useCallback(() => {
    setView("map");
    setCreateCoords(null);
  }, []);

  const handleDeletePin = useCallback(
    async (id: string) => {
      try {
        await pinStore.removePin(id);
        hapticNotification("success");
      } catch {
        hapticNotification("error");
      }
      setView("map");
      setSelectedPin(null);
    },
    [pinStore.removePin]
  );

  const handleDetailClose = useCallback(() => {
    setView("map");
    setSelectedPin(null);
  }, []);

  const handleCategoryChange = useCallback((cat: PinCategory | null) => {
    setCategoryFilter(cat);
  }, []);

  // ── Render states ──

  if (geo.loading) {
    return <LoadingScreen message="Getting your location..." />;
  }

  if (geo.error) {
    return <ErrorScreen message={geo.error} onRetry={geo.refresh} />;
  }

  if (!geo.position) {
    return <ErrorScreen message="Unable to determine your location." onRetry={geo.refresh} />;
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "var(--tg-bg)",
      }}
    >
      {/* Map */}
      <Map
        center={geo.position}
        pins={pinStore.pins}
        onPinClick={handlePinClick}
        onDropPin={handleDropPin}
        showDropButton={view === "map"}
      />

      {/* Category filter bar */}
      {view === "map" && (
        <CategoryFilter active={categoryFilter} onChange={handleCategoryChange} />
      )}

      {/* Pin count indicator */}
      {view === "map" && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(14,27,76,0.85)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            padding: "10px 20px",
            borderRadius: 9999,
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
            fontSize: 12,
            fontWeight: 600,
            color: "#a0a9d5",
            letterSpacing: "0.02em",
            zIndex: 1000,
            whiteSpace: "nowrap",
          }}
        >
          {pinStore.loading
            ? "Loading..."
            : pinStore.pins.length === 0
              ? "No pins nearby — tap + to drop one"
              : `${pinStore.pins.length} pin${pinStore.pins.length === 1 ? "" : "s"} nearby`}
        </div>
      )}

      {/* Pin Detail sheet */}
      {view === "detail" && selectedPin && (
        <PinDetail
          pin={selectedPin}
          isOwner={currentUserId === selectedPin.telegram_user_id}
          onClose={handleDetailClose}
          onDelete={handleDeletePin}
        />
      )}

      {/* Create Pin sheet */}
      {view === "create" && createCoords && (
        <CreatePin
          lat={createCoords.lat}
          lng={createCoords.lng}
          onSubmit={handleCreateSubmit}
          onCancel={handleCreateCancel}
        />
      )}
    </div>
  );
}
